import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material'
import Divider from '@mui/material/Divider'
import { useAtomValue } from 'jotai'
import * as React from 'react'
import { useNavigate } from 'react-router'

import LoginCard from '../components/cards/LoginCard'
import InstructorBookingHistory from '../components/InstructorBookingHistory'
import StudentBookingHistory from '../components/StudentBookingHistory'
import { viewTypeAtom } from '../contexts/AtomUtil'
import { isAuthenticatedAtom } from '../domains/auth/AuthSelectors'
import { isInstructorAtom } from '../domains/auth/AuthSelectors'
import { currentUserAtom } from '../domains/user/AtomUsers'
import { apiClient } from '../infra/http/apiClient'
import { copyToClipboard } from '../utils/utility'

export default function History() {
  const user = useAtomValue(currentUserAtom)
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const isInstructor = useAtomValue(isInstructorAtom)

  const viewType = useAtomValue(viewTypeAtom)
  const navigate = useNavigate()
  const [calendarUrl, setCalendarUrl] = React.useState('')
  const [webcal, setWebcal] = React.useState('')
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  const handleGetCalendarUrl = async () => {
    if (!isAuthenticated) return

    try {
      setLoading(true)
      const response = await apiClient.post(`/calendar/generate-url?userId=${user.resourceId}`)
      if (response?.data?.url) {
        setCalendarUrl(response.data.url)
        setWebcal(response.data.webcal)
        setDialogOpen(true)
      }
    } catch (error) {
      console.error('Error fetching calendar URL:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
  }

  if (!isAuthenticated) {
    return <LoginCard title="Sign in to View History" afterLogin={() => navigate('/mysessions')} />
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ width: '100%', pt: 0, height: '100%' }}>
        {/* 内容显示区域 */}
        {isInstructor && viewType === 'instructor' ? <InstructorBookingHistory /> : <StudentBookingHistory />}
      </Box>

      <Box
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          zIndex: 30,
          pointerEvents: 'none',
        }}
      >
        <Button
          onClick={handleGetCalendarUrl}
          disabled={loading}
          color="primary"
          size="medium"
          variant="contained"
          startIcon={<CalendarMonthIcon />}
          sx={{
            borderRadius: 28,
            minWidth: 200,
            boxShadow: 2,
            pointerEvents: 'auto',
            px: 3,
            py: 1,
            textTransform: 'none',
          }}
        >
          Subscribe to Calendar
        </Button>
      </Box>

      {/* 日历订阅对话框 */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Subscribe to Your Sessions Calendar</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Click the button below to add your sessions to your calendar application:
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              window.open(webcal)
            }}
            startIcon={<CalendarMonthIcon />}
            color="primary"
            size="medium"
            sx={{
              textAlign: 'left',
              flex: 1,
              textTransform: 'none',
              px: 2,
              py: 1,
              mt: 1,
              mb: 1,
            }}
          >
            Add to Calendar
          </Button>
          <Divider sx={{ mt: 2, mb: 2 }} />
          <Typography variant="body1" gutterBottom>
            If your device does not support webcal, you can copy the URL below and add it to your calendar application:
          </Typography>
          <Box
            sx={{
              bgcolor: 'background.paper',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              mt: 2,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-all',
                flex: 1,
                mr: 1,
              }}
            >
              {calendarUrl}
            </Typography>
            <IconButton size="small" onClick={() => copyToClipboard(calendarUrl)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Instructions:
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ pl: 2, mt: 1 }}>
              <li>For Apple Calendar: Go to File &gt; New Calendar Subscription</li>
              <li>For Google Calendar: Go to Settings &gt; Add Calendar &gt; From URL</li>
              <li>For Outlook: Go to Calendar view &gt; Add Calendar &gt; From Internet</li>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}
